/**
 * Client-side sort for SMSWiser drops list rows.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { SmswiserDrop } from '@/types/smswiser-drops';
import { deriveDropStatusLabel } from '@/features/drops/utils/dropStatus';
import { parseSmswiserDate } from '@/features/drops/utils/smswiserDate';

export type DropsSortKey = 'timing' | 'amount' | 'country' | 'campaign' | 'status';
export type DropsSortDir = 'asc' | 'desc';

export interface DropsSortState {
  sortBy: DropsSortKey;
  sortDir: DropsSortDir;
}

export const DEFAULT_DROPS_SORT: DropsSortState = {
  sortBy: 'timing',
  sortDir: 'desc',
};

const VALID_SORT_KEYS = new Set<DropsSortKey>([
  'timing',
  'amount',
  'country',
  'campaign',
  'status',
]);

export function parseDropsSortKey(raw: string | null): DropsSortKey {
  if (raw && VALID_SORT_KEYS.has(raw as DropsSortKey)) {
    return raw as DropsSortKey;
  }
  return DEFAULT_DROPS_SORT.sortBy;
}

export function parseDropsSortDir(raw: string | null): DropsSortDir {
  return raw === 'asc' ? 'asc' : DEFAULT_DROPS_SORT.sortDir;
}

export function defaultSortDir(key: DropsSortKey): DropsSortDir {
  return key === 'timing' || key === 'amount' ? 'desc' : 'asc';
}

export function isDefaultDropsSort(sort: DropsSortState): boolean {
  return (
    sort.sortBy === DEFAULT_DROPS_SORT.sortBy && sort.sortDir === DEFAULT_DROPS_SORT.sortDir
  );
}

export function toggleDropsSort(
  current: DropsSortState,
  key: DropsSortKey,
): DropsSortState {
  if (current.sortBy === key) {
    return { sortBy: key, sortDir: current.sortDir === 'asc' ? 'desc' : 'asc' };
  }
  return { sortBy: key, sortDir: defaultSortDir(key) };
}

/** SMSWiser "Start" column: startedAt, else executeAfter, else finishedAt. */
function dropStartColumnMs(drop: SmswiserDrop): number {
  const startedAt = drop.status?.startedAt?.trim();
  if (startedAt) {
    const ms = parseSmswiserDate(startedAt)?.getTime();
    if (ms != null) return ms;
  }
  const executeAfter = drop.executeAfter?.trim();
  if (executeAfter) {
    const ms = parseSmswiserDate(executeAfter)?.getTime();
    if (ms != null) return ms;
  }
  const finishedAt = drop.finishedAt?.trim();
  if (finishedAt) {
    const ms = parseSmswiserDate(finishedAt)?.getTime();
    if (ms != null) return ms;
  }
  return 0;
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

function compareDrops(a: SmswiserDrop, b: SmswiserDrop, sort: DropsSortState): number {
  let cmp = 0;
  switch (sort.sortBy) {
    case 'timing':
      cmp = dropStartColumnMs(a) - dropStartColumnMs(b);
      break;
    case 'amount':
      cmp = a.cap - b.cap;
      break;
    case 'country':
      cmp = compareText(a.country ?? '', b.country ?? '');
      break;
    case 'campaign':
      cmp = compareText(a.campaign ?? '', b.campaign ?? '');
      break;
    case 'status':
      cmp = compareText(deriveDropStatusLabel(a), deriveDropStatusLabel(b));
      break;
    default:
      cmp = 0;
  }
  if (cmp !== 0) return sort.sortDir === 'asc' ? cmp : -cmp;
  return b.id - a.id;
}

export interface SortDropsOptions {
  /** Keep SMSWiser /drops/all row order (newest drops first from upstream). */
  preserveSmswiserOrder?: boolean;
}

export function sortDrops(
  drops: SmswiserDrop[],
  sort: DropsSortState,
  options?: SortDropsOptions,
): SmswiserDrop[] {
  if (options?.preserveSmswiserOrder) return drops;
  return [...drops].sort((a, b) => compareDrops(a, b, sort));
}
